import { roomEvents } from "@closerplatform/closer-sdk";
import {
  ConversationStatus,
  InboxEntry as APIInboxEntry,
  UserRole
} from "@closerplatform/spinner-openapi";
import { MessageTag } from "@fidget/common/models/messageTag";
import { logger } from "@fidget/common/utils";
import { flow, Instance, SnapshotIn, types } from "mobx-state-tree";
import { Observable, Subject } from "rxjs";
import { IAgent } from "../agent/Agent";
import { AgentReferenceType } from "../agent/types";
import { TypingStore } from "../typing/TypingStore";
import {
  agentStoreGetter,
  callStoreGetter,
  currentUserProfileGetter,
  guestStoreGetter,
  inboxStoreGetter,
  servicesGetters,
  uiStoreGetter,
  widgetStoreGetter
} from "../utils";
import { Guest, IGuest } from "./Guest";

// Inline with @closerplatform/spinner-openapi InboxEntryType
// Due to openapi generator limitation (wrong names' case) it is rewritten here
// and should be used instead of InboxEntryType
export enum InboxEntryTypeEnum {
  MESSAGE = "MESSAGE",
  NOTE = "NOTE",
  FILES = "FILES",
  MEETING_SCHEDULED = "MEETING_SCHEDULED",
  MEETING_RESCHEDULED = "MEETING_RESCHEDULED",
  MEETING_CANCELLED = "MEETING_CANCELLED",
  CALL_STARTED = "CALL_STARTED",
  CALL_ENDED = "CALL_ENDED",
  FORM = "FORM"
}

export enum SnoozedStatus {
  Snoozed = "snoozed",
  Unsnoozed = "unsnoozed"
}
export interface UserTypingEvent {
  profile: IAgent | IGuest;
  preview?: string;
}

export const Conversation = types
  .model("Conversation", {
    id: types.identifier, // room
    roomId: types.string,
    assignee: types.maybe(AgentReferenceType),
    followers: types.array(AgentReferenceType),
    timestamp: types.number,
    threadCreatedAt: types.number,
    threadClosedAt: types.maybe(types.number),
    lastAdviserTimestamp: types.maybe(types.number),
    lastGuestTimestamp: types.number,
    mark: types.number,
    message: types.maybe(types.string),
    // TODO: decide whether owner should reference global guest store
    owner: Guest,
    senderId: types.maybe(types.string),
    status: types.enumeration<ConversationStatus>(
      "ConversationStatus",
      Object.values(ConversationStatus)
    ),
    lastItemType: types.enumeration<InboxEntryTypeEnum>(
      "InboxEntryType",
      Object.values(InboxEntryTypeEnum)
    ),
    unreadCount: types.number,
    tags: types.array(types.string),
    isSnoozed: types.boolean,
    typingStore: types.maybe(TypingStore),
    snoozedStatus: types.maybe(
      types.enumeration<SnoozedStatus>("SnoozedStatus", Object.values(SnoozedStatus))
    )
  })
  .views(servicesGetters)
  .views(guestStoreGetter)
  .views(agentStoreGetter)
  .views(currentUserProfileGetter)
  .views(callStoreGetter)
  .views(widgetStoreGetter)
  .views(inboxStoreGetter)
  .views(uiStoreGetter)
  .extend(() => {
    const unsubscribe = new Subject<void>();

    return {
      views: {
        get unsubscribeRoomListeners$(): Observable<void> {
          return unsubscribe.asObservable();
        }
      },
      actions: {
        unsubscribeRoomListeners(): void {
          unsubscribe.next();
        }
      }
    };
  })
  .views((self) => {
    const views = {
      /**
       * Last message's sender, can be both agent or guest (or undefined)
       */
      get sender() {
        if (self.senderId) {
          // XXX: only *schematically valid* possibility for a sender to be a guest is the owner
          if (self.owner && self.owner.id === self.senderId) {
            return self.owner;
          }
          return self.agentStore && self.agentStore.getOrLoad(self.senderId);
        }
        return undefined;
      },
      /**
       * Are there any users currently typing in the conversation?
       */
      get usersTypingEvents(): UserTypingEvent[] {
        if (!self.typingStore) {
          return [];
        }

        return self.typingStore
          .getTypingEvents()
          .map(({ userId, preview }) =>
            userId === self.owner.id
              ? { profile: self.owner, preview }
              : { profile: self.agentStore.getOrLoad(userId), preview }
          );
      },
      /**
       * Does the user with specified id follow this conversation?
       */
      hasFollower(id: string) {
        return self.followers.some((f) => f.id === id);
      },
      /**
       * Is the conversation closed?
       */
      get isClosed() {
        logger.info(`Room status get: ${self.id} status=${self.status}`);
        return (
          self.status === ConversationStatus.Solved || self.status === ConversationStatus.Unsolved
        );
      },
      /**
       * Is the conversation assigned to me?
       */
      get isAssignedToMe(): boolean {
        return (
          (self.assignee &&
            self.currentUserProfile &&
            self.currentUserProfile.id === self.assignee.id) ||
          false
        );
      }
    };
    return views;
  })
  .actions((self) => {
    const actions = {
      updateTopics: flow(function* (topicNames: string[]) {
        try {
          yield self.spinnerService.patchGuestProfile(self.owner.id, { topics: topicNames });
          self.owner.setTopics(topicNames);
        } catch (e) {
          logger.error({ message: "Couldn't patch guest topics", error: e });
          self.uiStore.addGenericErrorToast();
        }
      }),

      resetSnoozedStatus() {
        self.isSnoozed = false;
        self.snoozedStatus = undefined;
      },
      /**
       * Set conversation status
       */
      setStatus(status: ConversationStatus): void {
        logger.info(`Room status set: ${self.id} status=${status}`);
        self.status = status;

        if (status === ConversationStatus.Solved || status === ConversationStatus.Unsolved) {
          actions.resetSnoozedStatus();
        }
      },
      /**
       * Set unread message count
       */
      setUnreadCount(unreadCount: number): void {
        self.unreadCount = unreadCount;
      },
      /**
       * Add agent to conversation followers
       * @param {string} id  agent id
       */
      addFollower(id: string) {
        if (!self.hasFollower(id)) {
          self.followers.push(id);
        }
      },
      /**
       * Remove agent from conversation followers
       * @param {string} id  agent id
       */
      removeFollower(id: string) {
        self.followers.replace(self.followers.filter((f) => f.id !== id));
      },
      /**
       * Remove all followers from conversation
       */
      removeFollowers() {
        self.followers.clear();
      },
      /**
       * Set or unset agent as conversation assignee
       * @param {string|undefined} id  agent id or undefined
       */
      setAssignee(id: string | undefined) {
        // FIXME: MST typescript bug - false type error when assigning identifier to reference type
        self.assignee = id as any;

        actions.resetSnoozedStatus();
      },

      setLastAdviserTimestamp(timestamp: number) {
        self.lastAdviserTimestamp = timestamp;
      },

      removeLastAdivserTimestamp() {
        self.lastAdviserTimestamp = undefined;
      },

      /**
       * (Partially) update properties
       */
      update(partial: Partial<SnapshotIn<typeof self>>) {
        const { id, ...data } = partial;
        if (id && id !== self.id) {
          throw new Error("Attempted to update object with a different ID");
        }
        Object.assign(self, data);
      },
      /**
       * Set last message fields
       */
      setLastMessage(
        senderId: string,
        message: string,
        timestamp: number,
        type: InboxEntryTypeEnum
      ): void {
        self.lastItemType = type;
        self.message = message;
        self.senderId = senderId;
        self.timestamp = timestamp;

        // sender is updated after setting senderId, so we can use it right away
        if (self.sender && self.sender.role === UserRole.LEAD) {
          self.lastGuestTimestamp = timestamp;
        } else if (self.sender && self.sender.role === UserRole.ADVISER) {
          self.lastAdviserTimestamp = timestamp;
        }

        if (
          self.callStore.isNoCall &&
          self.isAssignedToMe &&
          self.userActivityService.isPageInactive()
        ) {
          self.ringtoneService.playMessageSoundForInactiveTab();
        }
      },

      udpateFromChatMessage(event: roomEvents.MessageSent): void {
        actions.setLastMessage(
          event.authorId,
          event.message,
          event.timestamp,
          InboxEntryTypeEnum.MESSAGE
        );
      },

      udpateFromChatCustomMessage(event: roomEvents.CustomMessageSent): void {
        const { subtag, authorId, timestamp, message } = event;

        if (Object.values(InboxEntryTypeEnum).includes(subtag as InboxEntryTypeEnum)) {
          const content =
            message || subtag === InboxEntryTypeEnum.NOTE ? InboxEntryTypeEnum.NOTE : "";

          actions.setLastMessage(authorId, content, timestamp, subtag as InboxEntryTypeEnum);
        }
        if (subtag === MessageTag.MESSAGE_WITH_FORM || subtag === MessageTag.FORM_SUBMITTED) {
          actions.setLastMessage(authorId, message, timestamp, InboxEntryTypeEnum.FORM);
        }
      },

      /**
       * Register user as typing or update his preview if already exists
       * @param {string} userId agent or guest ID
       */
      updateTypingUser(userId: string, preview?: string) {
        if (!self.typingStore) {
          self.typingStore = TypingStore.create();
        }

        self.typingStore.updateTypingUser(userId, preview);
      },
      /**
       * Unregister user as typing
       * @param {string} userId agent or guest ID
       */
      removeTypingUser(userId: string) {
        if (!self.typingStore) {
          self.typingStore = TypingStore.create();
        }

        self.typingStore.removeTypingUser(userId);
      },

      /**
       * Unregister all users as typing
       */
      clearTypingUsers() {
        if (!self.typingStore) {
          self.typingStore = TypingStore.create();
        }

        self.typingStore.clearTypingUsers();
      },

      snoozeConversation() {
        self.isSnoozed = true;
        self.snoozedStatus = SnoozedStatus.Snoozed;
      },
      unsnoozeConversation() {
        self.isSnoozed = false;
        self.snoozedStatus = SnoozedStatus.Unsnoozed;
      }
    };
    return actions;
  })
  .actions((self) => {
    const actions = {
      toggleSnooze: flow(function* () {
        try {
          if (!self.isClosed) {
            yield self.isSnoozed
              ? self.spinnerService.unsnooze(self.roomId)
              : self.spinnerService.snooze(self.roomId);
          }
        } catch (error) {
          logger.error({ message: "Couldn't toggle snooze", error });
          throw new Error(`Could not snooze/unsnooze the conversation with id: '${self.id}'`);
        }
      })
    };
    return actions;
  });

export interface IConversation extends Instance<typeof Conversation> {}
export interface IConversationSnapshotIn extends SnapshotIn<typeof Conversation> {}

export function conversationResourceDataToSnapshot(
  data: APIInboxEntry
): SnapshotIn<typeof Conversation> {
  const {
    room,
    type,
    owner,
    assignee,
    profile,
    ownerPresence,
    sender,
    threadCreatedAt,
    snoozedStatus,
    tagGroupId,
    ...rest
  } = data;

  const guest: SnapshotIn<typeof Guest> = {
    id: owner,
    status: ownerPresence,
    roomId: room,
    tagGroupId,
    ...profile
  };

  return {
    id: room,
    roomId: room,
    assignee,
    senderId: sender,
    owner: guest,
    lastItemType: type as unknown as InboxEntryTypeEnum,
    // TODO: threadCreatedAt will be changed from optional to mandatory in SDK - monitor and adjust here
    threadCreatedAt: threadCreatedAt || 0,
    snoozedStatus: snoozedStatus as SnoozedStatus | undefined,
    ...rest
  };
}
